import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection, doc, getDoc, getDocs,
  updateDoc, query, where,
} from "firebase/firestore";
import ChatScreen from "./ChatScreen";
import ContactsScreen from "./ContactsScreen";
import TasksScreen from "./TasksScreen";
import WaterScreen from "./WaterScreen";
import PollScreen from "./PollScreen";
import ReportsScreen from "./ReportsScreen";
import BankScreen from "./BankScreen";
import { UpiPayModal, computeMaintenanceLedger } from "./Dashboard";
import { AddExpenseModal, Modal, MonthChips } from "./PassbookScreen";
import { BylawsPDFSection } from "./BylawsScreen";
import adyantamLogo from "../assets/adyantam-logo.png";

// ══════════════════════════════════════════════════════════════
// HELPERS
// Small, pure, presentation-only helpers are kept as local copies
// here rather than imported — same pattern already used elsewhere
// in the app (e.g. computeMonthlyRate in Dashboard.jsx / FlatsScreen.jsx).
// Only the two pieces of genuinely complex shared LOGIC — the
// Maintenance "Brought Forward" formula and the Pocket Expense
// approval workflow — are imported from Dashboard.jsx and
// PassbookScreen.jsx instead of being duplicated. If any of the
// helpers below are ever changed in Dashboard.jsx or PassbookScreen.jsx,
// mirror the change here too.
// ══════════════════════════════════════════════════════════════
function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

// Mirrors fyMonthKeys() in Dashboard.jsx — Indian financial year, Apr → current month.
function fyMonthKeys() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const fyStartYear = m >= 4 ? y : y - 1;
  const months = [];
  let cy = fyStartYear, cm = 4;
  while (true) {
    months.push(`${cy}-${String(cm).padStart(2, "0")}`);
    if (cy === y && cm === m) break;
    cm++;
    if (cm > 12) { cm = 1; cy++; }
  }
  return months;
}

function fmt(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN");
}

function formatMonth(key) {
  if (!key) return "";
  const [y, m] = key.split("-");
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[parseInt(m, 10) - 1]}-${y}`;
}

// Mirrors fmtMonth() in PassbookScreen.jsx — used for the Passbook month chips.
function fmtMonthShort(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(mo) - 1]}-${String(y).slice(2)}`;
}

// Mirrors prevMonthKey() in PassbookScreen.jsx.
function prevMonthKey(m) {
  if (!m) return null;
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Mirrors getFYMonths() in PassbookScreen.jsx — {key,label} list for MonthChips.
function getFYMonths() {
  const now = new Date();
  const todayKey = currentMonthKey();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const months = [];
  for (let i = 0; i < 12; i++) {
    const m = (3 + i) % 12;
    const y = fyStartYear + Math.floor((3 + i) / 12);
    const key = `${y}-${String(m + 1).padStart(2, "0")}`;
    if (key > todayKey) break;
    months.push({ key, label: fmtMonthShort(key) });
  }
  return months;
}

function maskAadhar(aadhar) {
  if (!aadhar || aadhar.length < 4) return "—";
  return `XXXX XXXX ${aadhar.slice(-4)}`;
}

// ── Tabs — matches the current flat-owner dashboard's tab list ──
const TENANT_TABS = [
  { id: "dashboard", label: "Dashboard", icon: "📊" },
  { id: "flats",     label: "Flats",     icon: "🏠" },
  { id: "passbook",  label: "Passbook",  icon: "📒" },
  { id: "water",     label: "Water",     icon: "💧" },
  { id: "poll",      label: "Poll",      icon: "🗳️" },
  { id: "tasks",     label: "Tasks",     icon: "✅" },
  { id: "chat",      label: "Chat",      icon: "💬" },
  { id: "contacts",  label: "Contacts",  icon: "📞" },
  { id: "bylaws",    label: "Bylaws",    icon: "📜" },
  { id: "reports",   label: "Reports",   icon: "📄" },
];

// ── Badge ──────────────────────────────────────────────────────
function Badge({ text, color }) {
  const colors = {
    green:  { bg: "#4caf5018", border: "#4caf5040", text: "#4caf50" },
    red:    { bg: "#ff525218", border: "#ff525240", text: "#ff5252" },
    amber:  { bg: "#ffa72618", border: "#ffa72640", text: "#ffa726" },
    blue:   { bg: "#42a5f518", border: "#42a5f540", text: "#42a5f5" },
    purple: { bg: "#6c63ff18", border: "#6c63ff40", text: "#6c63ff" },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "600",
    }}>{text}</span>
  );
}

// ── Section Header ─────────────────────────────────────────────
function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: "10px", marginTop: "4px" }}>
      <div style={{ fontSize: "12px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600" }}>
        {title}
      </div>
      {sub && <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>{sub}</div>}
    </div>
  );
}

// ── Ledger Table — same column layout as the flat-owner Dashboard ──
function LedgerTable({ title, icon, row }) {
  const cols = [
    { key: "carryForward",     label: "Brought Fwd" },
    { key: "presentMonthDue",  label: "This Month" },
    { key: "totalOutstanding", label: "Total" },
    { key: "paid",             label: "Paid" },
    { key: "paidDate",         label: "Paid Date" },
    { key: "balance",          label: "Balance" },
  ];
  const isOwed = (row.balance || 0) > 0;
  const balanceColor = isOwed ? "#ff5252" : "#4caf50";

  return (
    <div style={{ marginBottom: "16px" }}>
      <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0", marginBottom: "8px" }}>
        {icon} {title}
      </div>
      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40",
        borderRadius: "12px", overflowX: "auto",
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", minWidth: "480px" }}>
          <thead>
            <tr>
              {cols.map(c => (
                <th key={c.key} style={{
                  textAlign: "right", padding: "8px 10px", color: "#8888a8",
                  fontWeight: "600", fontSize: "10px", textTransform: "uppercase",
                  letterSpacing: "0.3px", borderBottom: "1px solid #2e2e40", whiteSpace: "nowrap",
                }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ textAlign: "right", padding: "10px", color: "#e8e8f0", whiteSpace: "nowrap" }}>
                {fmt(row.carryForward)}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: "#e8e8f0", whiteSpace: "nowrap" }}>
                {fmt(row.presentMonthDue)}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: "#e8e8f0", fontWeight: "600", whiteSpace: "nowrap" }}>
                {fmt(row.totalOutstanding)}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: "#4caf50", whiteSpace: "nowrap" }}>
                {row.paid != null ? fmt(row.paid) : "—"}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: "#8888a8", whiteSpace: "nowrap" }}>
                {row.paidDate || "—"}
              </td>
              <td style={{ textAlign: "right", padding: "10px", color: balanceColor, fontWeight: "700", whiteSpace: "nowrap" }}>
                {fmt(row.balance)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// DASHBOARD TAB — "My Dues"
// Rebuilt fresh to match the current flat-owner "My Dues" layout.
// Uses the imported computeMaintenanceLedger() so the Brought
// Forward / This Month / Total math is guaranteed identical to
// the owner's Dashboard — not a second copy of that formula.
// Non-Payee Flats section intentionally excluded (owner-only view).
// ══════════════════════════════════════════════════════════════
function TenantMyDues({ user, settings, flats, payments, maintenanceBills, waterBills }) {
  const month = currentMonthKey();
  const today = new Date().getDate();
  const cutoff = settings?.dueCutoffDay || 10;

  const [showPayModal, setShowPayModal] = useState(false);
  const [declaring] = useState(false); // kept only so BillBanner's disabled styling matches Dashboard.jsx

  const myFlat = flats.find(f => f.flatId === user.flatId);

  // ── Who pays what — based on THIS tenant's own record ──────────
  const tenantPaysMaint   = user.paysMaintenance === true;
  const tenantPaysWater   = user.paysWater       === true;
  const tenantPaysBoth    = tenantPaysMaint && tenantPaysWater;
  const tenantPaysNeither = !tenantPaysMaint && !tenantPaysWater;

  // ── Maintenance ──────────────────────────────────────────────
  const maintPayment = payments.find(
    p => p.flatId === user.flatId && p.month === month && p.status !== "rejected" &&
         (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both")
  );
  const maintPaid    = maintPayment?.status === "confirmed";
  const maintOverdue = !maintPaid && today > cutoff;

  const ledger = computeMaintenanceLedger({ flat: myFlat, payments, maintenanceBills, settings, month });
  const maintenanceLedger = {
    carryForward:     ledger.broughtForward,
    presentMonthDue:  ledger.presentMonthDue,
    totalOutstanding: ledger.totalOutstanding,
    paid:             maintPaid ? ledger.presentMonthDue : null,
    paidDate:         maintPaid && maintPayment?.confirmedAt
                        ? new Date(maintPayment.confirmedAt?.seconds * 1000).toLocaleDateString("en-IN")
                        : null,
    balance:          ledger.totalOutstanding,
  };

  // ── Water — last billed month (mirrors Dashboard.jsx's fyMonthKeys logic) ──
  const fyMonths   = fyMonthKeys();
  const waterMonth = fyMonths.length >= 2 ? fyMonths[fyMonths.length - 2] : fyMonths[0];
  const myWaterBill = waterBills.find(w => w.flatId === user.flatId && w.month === waterMonth);
  const waterAmt = myWaterBill ? (myWaterBill.carryForward || 0) + (myWaterBill.billAmount || 0) : 0;

  const waterPayment = payments.find(
    p => p.flatId === user.flatId && p.month === month && p.status !== "rejected" &&
         (!p.type || p.type.toLowerCase() === "water" || p.type.toLowerCase() === "both")
  );
  const waterPaid    = myWaterBill?.paid || waterPayment?.status === "confirmed";
  const waterOverdue = !waterPaid && today > cutoff;

  const waterLedger = myWaterBill
    ? {
        carryForward:     myWaterBill.carryForward || 0,
        presentMonthDue:  myWaterBill.billAmount   || 0,
        totalOutstanding: (myWaterBill.carryForward || 0) + (myWaterBill.billAmount || 0),
        paid:             waterPaid ? (myWaterBill.billAmount || 0) : null,
        paidDate:         waterPaid && myWaterBill.paidDate
                            ? new Date(myWaterBill.paidDate).toLocaleDateString("en-IN")
                            : null,
        balance:          Math.abs(myWaterBill.balance ?? (myWaterBill.totalDue || 0)),
      }
    : { carryForward: 0, presentMonthDue: 0, totalOutstanding: 0, paid: null, paidDate: null, balance: 0 };

  // ── Status banner ────────────────────────────────────────────
  function BillBanner({ label, isPaid, isOverdue }) {
    let color = "#ffa726", text = "Pending", badge = "amber";
    if (isPaid)         { color = "#4caf50"; text = "Paid ✅"; badge = "green"; }
    else if (isOverdue) { color = "#ff5252"; text = "Overdue"; badge = "red"; }
    return (
      <div style={{
        background: "#1a1a24", border: `1px solid ${color}40`,
        borderLeft: `3px solid ${color}`,
        borderRadius: "14px", padding: "14px 16px", marginBottom: "12px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <span style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px", display: "block" }}>{label}</span>
          <Badge text={text} color={badge} />
        </div>
        {!isPaid && (
          <button disabled={declaring} onClick={() => setShowPayModal(true)} style={{
            background: declaring ? "#2e2e40" : "linear-gradient(135deg, #6c63ff, #4caf50)", color: "#fff",
            border: "none", borderRadius: "10px", padding: "10px 16px",
            fontSize: "13px", fontWeight: "700",
            cursor: declaring ? "not-allowed" : "pointer", opacity: declaring ? 0.7 : 1,
          }}>
            PAY
          </button>
        )}
        {isPaid && <span style={{ fontSize: "12px", color: "#4caf50" }}>✅ Confirmed by MC</span>}
      </div>
    );
  }

  return (
    <div>
      <SectionHeader title="My Dues" sub={`Flat ${user.flatId} · ${formatMonth(month)}`} />

      {/* Ledger tables — shown when this tenant is responsible; otherwise a plain note */}
      {tenantPaysMaint
        ? <LedgerTable title={`Maintenance — ${formatMonth(month)}`} icon="🏠" row={maintenanceLedger} />
        : <div style={{
            background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
            padding: "12px 16px", marginBottom: "14px", fontSize: "12px", color: "#555570",
          }}>🏠 Maintenance — paid by flat owner</div>
      }
      {tenantPaysWater
        ? <LedgerTable title={`Water — ${formatMonth(waterMonth)}`} icon="💧" row={waterLedger} />
        : <div style={{
            background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
            padding: "12px 16px", marginBottom: "14px", fontSize: "12px", color: "#555570",
          }}>💧 Water — paid by flat owner</div>
      }

      {/* PAY banner — combined if both, single if only one, none if neither */}
      {tenantPaysBoth && (
        <BillBanner
          label="Maintenance & Water"
          isPaid={maintPaid && waterPaid}
          isOverdue={maintOverdue || waterOverdue}
        />
      )}
      {!tenantPaysBoth && tenantPaysMaint && (
        <BillBanner label="Maintenance" isPaid={maintPaid} isOverdue={maintOverdue} />
      )}
      {!tenantPaysBoth && tenantPaysWater && (
        <BillBanner label={`Water — ${formatMonth(waterMonth)}`} isPaid={waterPaid} isOverdue={waterOverdue} />
      )}
      {tenantPaysNeither && (
        <div style={{
          background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px",
          padding: "12px 16px", marginBottom: "14px", fontSize: "12px", color: "#555570",
        }}>
          You are not currently responsible for any dues on this flat — the flat owner pays both Maintenance and Water.
        </div>
      )}

      {/* UPI Pay Modal — reused from Dashboard.jsx, pre-filled with only what this tenant pays */}
      {showPayModal && (
        <UpiPayModal
          flatId={user.flatId}
          settings={settings}
          maintAmt={maintenanceLedger.totalOutstanding}
          maintDue={tenantPaysMaint && !maintPaid}
          waterAmt={waterAmt}
          waterDue={tenantPaysWater && !waterPaid}
          waterMonth={waterMonth}
          adhocItems={[]}
          onClose={() => setShowPayModal(false)}
        />
      )}

      {/* Note about owner liability */}
      <div style={{
        background: "#ffa72610", border: "1px solid #ffa72630",
        borderRadius: "10px", padding: "10px 14px",
        fontSize: "11px", color: "#ffa726", marginBottom: "16px",
      }}>
        ⚠ Note: Flat owner is ultimately liable to the society for any unpaid dues.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PASSBOOK TAB — new tab, built fresh (PassbookScreen.jsx itself
// is untouched). Maintenance/Water lines are filtered by who pays
// what; Pocket Expense reuses the exact same approval-workflow
// code as the owner's Passbook (AddExpenseModal, Modal, MonthChips
// — imported, not duplicated).
// ══════════════════════════════════════════════════════════════
function TenantPassbookTab({ user }) {
  const [showBank, setShowBank]     = useState(false);
  const fyMonths = getFYMonths();
  const [filterMonth, setFilterMonth] = useState(currentMonthKey());
  const [payments,      setPayments]      = useState([]);
  const [waterBills,    setWaterBills]    = useState([]);
  const [maintBills,    setMaintBills]    = useState([]);
  const [expenses,      setExpenses]      = useState([]);
  const [adhocPayments, setAdhocPayments] = useState([]);
  const [adhocEvents,   setAdhocEvents]   = useState({});
  const [loading,       setLoading]       = useState(true);
  const [showAdd,       setShowAdd]       = useState(false);
  const [showArchive,   setShowArchive]   = useState(false);
  const [refreshKey,    setRefreshKey]    = useState(0);

  const tenantPaysMaint = user.paysMaintenance === true;
  const tenantPaysWater = user.paysWater       === true;

  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const FY_START_KEY = `${fyStartYear}-04`;

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const pSnap = await getDocs(query(collection(db, "payments"), where("flatId", "==", user.flatId)));
        setPayments(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const wSnap = await getDocs(query(collection(db, "waterBills"), where("flatId", "==", user.flatId)));
        setWaterBills(wSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const mSnap = await getDocs(query(collection(db, "maintenanceBills"), where("flatId", "==", user.flatId)));
        setMaintBills(mSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const eSnap = await getDocs(query(
          collection(db, "expenses"),
          where("addedBy", "==", user.name),
          where("isPocket", "==", true)
        ));
        setExpenses(eSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const aSnap = await getDocs(query(collection(db, "adhocPayments"), where("flatId", "==", user.flatId)));
        const adhocList = aSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAdhocPayments(adhocList);

        const missingTitleIds = [...new Set(
          adhocList.filter(a => !a.eventTitle).map(a => a.eventId).filter(Boolean)
        )];
        if (missingTitleIds.length > 0) {
          const evMap = {};
          await Promise.all(missingTitleIds.map(async (eid) => {
            try {
              const evSnap = await getDoc(doc(db, "adhocEvents", eid));
              if (evSnap.exists()) evMap[eid] = evSnap.data().title || "Society Work";
            } catch { /* ignore */ }
          }));
          setAdhocEvents(evMap);
        }
      } catch (e) {
        console.error("TenantPassbookTab load error:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user.flatId, user.name, refreshKey]);

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading passbook…</div>;
  }

  const monthPayment  = payments.find(p => p.month === filterMonth && p.type === "Maintenance");
  const monthBill     = maintBills.find(b => b.month === filterMonth);
  const monthWater    = waterBills.find(w => w.month === filterMonth);
  const monthExpenses = expenses.filter(e => e.month === filterMonth);

  function getAdhocMonth(a) {
    if (a.paidDate && a.paidDate.length >= 7) return a.paidDate.slice(0, 7);
    if (a.addedAt) {
      const d = a.addedAt.toDate ? a.addedAt.toDate() : new Date(a.addedAt.seconds * 1000);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }
    return null;
  }
  function getAdhocTitle(a) {
    return a.eventTitle || adhocEvents[a.eventId] || "Society Work";
  }

  const currentFYAdhoc = adhocPayments.filter(a => { const m = getAdhocMonth(a); return m && m >= FY_START_KEY; });
  const archiveAdhoc   = adhocPayments.filter(a => { const m = getAdhocMonth(a); return !m || m < FY_START_KEY; });

  // Build entries for the selected month — Maintenance/Water lines
  // only appear if THIS tenant is the one responsible for paying them.
  const entries = [];

  if (tenantPaysMaint && (monthBill || monthPayment)) {
    entries.push({
      id: `maintenance-${filterMonth}`,
      type: "Maintenance", icon: "🏠", month: filterMonth,
      amount: monthBill?.amountDue ?? monthBill?.balance ?? monthBill?.rate ?? monthPayment?.amount ?? 0,
      status: monthPayment ? monthPayment.status : (monthBill?.paid ? "confirmed" : "pending"),
      date: monthPayment?.paidAt || monthPayment?.confirmedAt || monthBill?.paidDate || null,
      note: monthPayment?.autoConfirmed ? "Gmail auto-confirmed" : "",
    });
  }

  if (tenantPaysWater && monthWater) {
    entries.push({
      id: `water-${filterMonth}`,
      type: "Water", icon: "💧", month: filterMonth,
      monthLabel: `${fmtMonthShort(prevMonthKey(filterMonth))} usage bill`,
      amount: monthWater.billAmount || monthWater.totalDue || 0,
      status: monthWater.paid ? "confirmed" : "pending",
      date: monthWater.paidDate || null,
      note: "",
    });
  }

  monthExpenses.forEach(e => {
    entries.push({
      id: e.id, type: "Pocket Expense", icon: "🧾", month: e.month, amount: e.amount,
      status: e.approvalStatus === "paid" ? "confirmed"
            : e.approvalStatus === "approved" ? "approved"
            : e.approvalStatus === "pending_president_approval" ? "pending"
            : e.approvalStatus === "pending_president" ? "pending"
            : "rejected",
      customStatusLabel: e.approvalStatus === "paid" ? "Paid"
            : e.approvalStatus === "approved" ? "Approved — Ready for Payment"
            : e.approvalStatus === "pending_president_approval" ? "Secretary approved — pending President approval"
            : e.approvalStatus === "pending_president" ? "Pending Secretary"
            : "Rejected",
      date: e.paidAt || null,
      note: e.description || "",
    });
  });

  currentFYAdhoc.forEach(a => {
    const eventMonth = getAdhocMonth(a);
    if (eventMonth === filterMonth) {
      entries.push({
        id: a.id, type: getAdhocTitle(a), icon: "🏗️", month: eventMonth, amount: a.amount,
        status: a.paid ? "confirmed" : "pending",
        date: a.paidDate || null,
        note: a.paid ? "Paid" : "Due — Society Work",
      });
    }
  });

  function statusColor(s) {
    if (s === "confirmed") return "green";
    if (s === "approved")  return "amber";
    if (s === "pending")   return "amber";
    if (s === "rejected")  return "red";
    return "amber";
  }
  function statusLabel(s) {
    if (s === "confirmed") return "Paid";
    if (s === "approved")  return "Approved";
    if (s === "pending")   return "Pending";
    if (s === "rejected")  return "Rejected";
    return "Pending";
  }

  return (
    <div style={{ padding: "0 0 24px" }}>
      <div style={{ marginBottom: "14px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
        <div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>📒 My Passbook</div>
          <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
            Flat {user.flatId} · Financial Year Apr-{new Date().getFullYear()} onwards
          </div>
        </div>
        <button onClick={() => setShowBank(true)} style={{
          background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "10px",
          padding: "8px 12px", color: "#42a5f5", fontSize: "12px", fontWeight: "600",
          cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
        }}>
          🏦 Bank Details
        </button>
      </div>

      {showBank && (
        <Modal title="🏦 Bank & UPI Details" onClose={() => setShowBank(false)}>
          <BankScreen isPresident={false} />
        </Modal>
      )}

      <MonthChips months={fyMonths} active={filterMonth} onSelect={setFilterMonth} />

      {entries.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "40px 20px", color: "#555570", fontSize: "13px",
          background: "#1a1a24", borderRadius: "12px", border: "1px solid #2e2e40", marginTop: "8px",
        }}>
          No transactions for {fmtMonthShort(filterMonth)}.
        </div>
      ) : (
        <div style={{ marginTop: "8px" }}>
          {entries.map(e => (
            <div key={e.id} style={{
              background: "#1a1a24", border: "1px solid #2e2e40",
              borderRadius: "12px", padding: "12px 14px", marginBottom: "8px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <div style={{ fontSize: "20px" }}>{e.icon}</div>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{e.type}</div>
                  <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
                    {e.monthLabel || fmtMonthShort(e.month)}{e.note ? ` · ${e.note}` : ""}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(e.amount)}</div>
                <Badge text={e.customStatusLabel || statusLabel(e.status)} color={statusColor(e.status)} />
              </div>
            </div>
          ))}
        </div>
      )}

      {filterMonth <= currentMonthKey() && (
        <div style={{ marginTop: "16px" }}>
          <button onClick={() => setShowAdd(true)} style={{
            width: "100%", padding: "12px", borderRadius: "12px",
            background: "#1a1a24", border: "1px dashed #2e2e40",
            color: "#6c63ff", fontSize: "13px", fontWeight: "600", cursor: "pointer",
          }}>
            + Submit Pocket Expense
          </button>
          <div style={{ fontSize: "11px", color: "#555570", textAlign: "center", marginTop: "6px" }}>
            Paid something from your pocket for the society? Submit for MC approval.
          </div>
        </div>
      )}

      {archiveAdhoc.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <div style={{ textAlign: "center" }}>
            <button onClick={() => setShowArchive(v => !v)} style={{
              background: "none", border: "none", color: "#6c63ff", fontSize: "13px", fontWeight: "600", cursor: "pointer",
            }}>
              {showArchive ? "▲ Hide Archive" : `▼ Archive (${archiveAdhoc.length} previous year entries)`}
            </button>
          </div>
          {showArchive && (
            <div style={{ marginTop: "10px", opacity: 0.8 }}>
              <div style={{ fontSize: "11px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600", marginBottom: "8px" }}>
                Society Work — Previous Years
              </div>
              {archiveAdhoc.map(a => {
                const m = getAdhocMonth(a);
                let fyLabel = "";
                if (m) {
                  const [y, mo] = m.split("-").map(Number);
                  const fyYear = mo >= 4 ? y : y - 1;
                  fyLabel = `FY ${fyYear}-${String(fyYear + 1).slice(2)}`;
                }
                let paidDateDisplay = "";
                if (a.paidDate && a.paidDate.length >= 10) {
                  const pd = new Date(a.paidDate);
                  if (!isNaN(pd)) paidDateDisplay = pd.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
                }
                return (
                  <div key={a.id} style={{
                    background: "#1a1a24", border: "1px solid #2e2e40",
                    borderRadius: "12px", padding: "12px 14px", marginBottom: "8px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <div style={{ fontSize: "20px" }}>🏗️</div>
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{getAdhocTitle(a)}</div>
                        <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
                          {fyLabel}{paidDateDisplay ? ` · Paid ${paidDateDisplay}` : ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(a.amount)}</div>
                      <Badge text={a.paid ? "Paid" : "Due"} color={a.paid ? "green" : "red"} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showAdd && (
        <AddExpenseModal
          user={user}
          isMC={false}
          regularCategories={[]}
          onClose={() => setShowAdd(false)}
          onDone={() => { setShowAdd(false); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// BYLAWS TAB — new tab, built fresh (BylawsScreen.jsx itself is
// untouched). Shows ONLY the bylaws PDF download section, reusing
// the exact same component the owner's Bylaws screen uses.
// Does NOT show Nomination of Representative (owner-only).
// ══════════════════════════════════════════════════════════════
function TenantBylawsTab() {
  return (
    <div>
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>📜 Bylaws</div>
      </div>
      <BylawsPDFSection />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// TENANT PROFILE TAB (inside Flats) — unchanged from the previous
// Tenant Screen. My Details, Payment Responsibility, Vehicle & UPI,
// Family Members.
// ══════════════════════════════════════════════════════════════
function TenantProfileTab({ user, onUpdate }) {
  const [form, setForm]   = useState({
    vehicleNos: (user.vehicleNos || []).join(", "),
    upiIds:     (user.upiIds     || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState("");

  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember]         = useState({ name: "", mobile: "", email: "", relation: "" });
  const [addingMember, setAddingMember]   = useState(false);
  const RELATIONS = ["Spouse","Son","Daughter","Parent","Sibling","Relative","Other"];

  async function handleSave() {
    setSaving(true); setError(""); setSaved(false);
    try {
      const vehicleArray = form.vehicleNos.split(",").map(v => v.trim()).filter(Boolean);
      const upiArray     = form.upiIds.split(",").map(u => u.trim()).filter(Boolean);
      await updateDoc(doc(db, "users", user.uid), {
        vehicleNos: vehicleArray,
        upiIds:     upiArray,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onUpdate) onUpdate({ ...user, vehicleNos: vehicleArray, upiIds: upiArray });
    } catch { setError("Save failed. Try again."); }
    finally { setSaving(false); }
  }

  async function handleAddMember() {
    if (!newMember.name.trim())                    { setError("Member name required."); return; }
    if (!/^\d{10}$/.test(newMember.mobile.trim()))  { setError("Valid 10-digit mobile required."); return; }
    if (!newMember.relation)                        { setError("Relation required."); return; }
    setAddingMember(true); setError("");
    try {
      const updated = [...(user.familyMembers || []), {
        name:     newMember.name.trim(),
        mobile:   newMember.mobile.trim(),
        email:    newMember.email.trim() || null,
        relation: newMember.relation,
      }];
      await updateDoc(doc(db, "users", user.uid), { familyMembers: updated });
      setNewMember({ name: "", mobile: "", email: "", relation: "" });
      setShowAddMember(false);
      if (onUpdate) onUpdate({ ...user, familyMembers: updated });
    } catch { setError("Failed to add member."); }
    finally { setAddingMember(false); }
  }

  const inputStyle = {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "10px", padding: "11px 14px", color: "#e8e8f0",
    fontSize: "14px", outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" };
  const fieldStyle = { marginBottom: "12px" };

  return (
    <div>
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>🏠 My Flat & Profile</div>
        <div style={{ fontSize: "12px", color: "#555570", marginTop: "3px" }}>Flat {user.flatId}</div>
      </div>

      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40",
        borderRadius: "12px", padding: "14px 16px", marginBottom: "16px",
      }}>
        <div style={{ fontSize: "12px", fontWeight: "600", color: "#6c63ff", marginBottom: "10px" }}>My Details</div>
        {[
          ["Name",    user.name],
          ["Mobile",  user.mobile],
          ["Email",   user.emailId || "—"],
          ["Aadhar",  maskAadhar(user.aadhar)],
          ["Entry",   user.entryDate || "—"],
        ].map(([label, value]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
            <span style={{ fontSize: "12px", color: "#555570" }}>{label}</span>
            <span style={{ fontSize: "12px", color: "#8888a8" }}>{value}</span>
          </div>
        ))}
      </div>

      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40",
        borderRadius: "12px", padding: "14px 16px", marginBottom: "16px",
      }}>
        <div style={{ fontSize: "12px", fontWeight: "600", color: "#6c63ff", marginBottom: "10px" }}>Payment Responsibility</div>
        <div style={{ display: "flex", gap: "8px" }}>
          {[
            { label: "🏠 Maintenance", pays: user.paysMaintenance, color: "#6c63ff" },
            { label: "💧 Water",       pays: user.paysWater,       color: "#06b6d4" },
          ].map(({ label, pays, color }) => (
            <div key={label} style={{
              flex: 1, padding: "8px", borderRadius: "8px", textAlign: "center",
              background: pays ? `${color}20` : "#0f0f13",
              border: `1px solid ${pays ? `${color}60` : "#2e2e40"}`,
            }}>
              <div style={{ fontSize: "11px", color: pays ? color : "#555570" }}>{label}</div>
              <div style={{ fontSize: "11px", color: pays ? color : "#555570", marginTop: "3px" }}>
                {pays ? "You pay" : "Owner pays"}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40",
        borderRadius: "12px", padding: "14px 16px", marginBottom: "16px",
      }}>
        <div style={{ fontSize: "12px", fontWeight: "600", color: "#6c63ff", marginBottom: "10px" }}>
          Vehicle & UPI — you can update these
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Vehicle Number(s) — separate with comma</label>
          <input style={inputStyle} value={form.vehicleNos}
            onChange={e => setForm(p => ({ ...p, vehicleNos: e.target.value }))}
            placeholder="e.g. TS09AB1234, TS09CD5678" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>UPI ID(s) — separate with comma</label>
          <input style={inputStyle} value={form.upiIds}
            onChange={e => setForm(p => ({ ...p, upiIds: e.target.value }))}
            placeholder="e.g. name@upi, 9876543210@paytm" />
        </div>

        {error && <div style={{ background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px" }}>⚠ {error}</div>}
        {saved && <div style={{ background: "#4caf5015", border: "1px solid #4caf5040", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", color: "#4caf50", marginBottom: "12px" }}>✅ Saved!</div>}

        <button onClick={handleSave} disabled={saving} style={{
          width: "100%", background: "#6c63ff", color: "#fff", border: "none",
          borderRadius: "10px", padding: "12px", fontSize: "14px", fontWeight: "600",
          cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
        }}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div style={{
        background: "#1a1a24", border: "1px solid #2e2e40",
        borderRadius: "12px", padding: "14px 16px", marginBottom: "16px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <div style={{ fontSize: "12px", fontWeight: "600", color: "#6c63ff" }}>Family Members</div>
          <button onClick={() => setShowAddMember(v => !v)} style={{
            background: "#6c63ff20", color: "#6c63ff", border: "1px solid #6c63ff40",
            borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer",
          }}>
            {showAddMember ? "Cancel" : "+ Add Member"}
          </button>
        </div>

        {(user.familyMembers || []).length === 0 && !showAddMember && (
          <div style={{ fontSize: "12px", color: "#555570" }}>No family members added yet.</div>
        )}

        {(user.familyMembers || []).map((m, i) => (
          <div key={i} style={{
            borderBottom: i < (user.familyMembers.length - 1) ? "1px solid #2e2e40" : "none",
            paddingBottom: "10px", marginBottom: "10px",
          }}>
            <div style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>
              {m.name} <span style={{ fontSize: "11px", color: "#555570", fontWeight: "400" }}>({m.relation})</span>
            </div>
            <div style={{ fontSize: "12px", color: "#8888a8", marginTop: "3px" }}>
              {m.mobile}{m.email ? ` · ${m.email}` : ""}
            </div>
          </div>
        ))}

        {showAddMember && (
          <div style={{ marginTop: "12px", borderTop: "1px solid #2e2e40", paddingTop: "12px" }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Full Name *</label>
              <input style={inputStyle} value={newMember.name}
                onChange={e => setNewMember(p => ({ ...p, name: e.target.value }))}
                placeholder="Family member name" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Mobile * (10 digits)</label>
              <input style={inputStyle} value={newMember.mobile}
                onChange={e => setNewMember(p => ({ ...p, mobile: e.target.value.replace(/\D/g,"").slice(0,10) }))}
                placeholder="Mobile number" inputMode="numeric" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Email (optional)</label>
              <input style={inputStyle} type="email" value={newMember.email}
                onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))}
                placeholder="Email address" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Relation *</label>
              <select style={{ ...inputStyle, cursor: "pointer" }} value={newMember.relation}
                onChange={e => setNewMember(p => ({ ...p, relation: e.target.value }))}>
                <option value="">Select relation</option>
                {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button onClick={handleAddMember} disabled={addingMember} style={{
              width: "100%", background: "#6c63ff", color: "#fff", border: "none",
              borderRadius: "10px", padding: "11px", fontSize: "13px", fontWeight: "600",
              cursor: addingMember ? "not-allowed" : "pointer", opacity: addingMember ? 0.7 : 1,
            }}>
              {addingMember ? "Adding…" : "Add Family Member"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN TENANT SCREEN
// ══════════════════════════════════════════════════════════════
export default function TenantScreen() {
  const { user, logout } = useAuth();
  const [activeTab,  setActiveTab]  = useState("dashboard");
  const [settings,   setSettings]   = useState(null);
  const [flats,      setFlats]      = useState([]);
  const [payments,   setPayments]   = useState([]);
  const [waterBills, setWaterBills] = useState([]);
  const [maintenanceBills, setMaintenanceBills] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [tenantUser, setTenantUser] = useState(user); // local copy for profile updates

  async function loadData() {
    setDataLoading(true);
    try {
      // Settings — same fixed doc used by Dashboard.jsx (not yet scoped per society)
      const settingsSnap = await getDoc(doc(db, "settings", "init"));
      if (settingsSnap.exists()) setSettings(settingsSnap.data());

      // Flats — scoped to this tenant's own society, matching Dashboard.jsx.
      // (The previous Tenant Screen pulled every flat from every society —
      // fixed here.)
      const flatsSnap = await getDocs(query(
        collection(db, "flats"),
        where("societyId", "==", user.societyId)
      ));
      setFlats(flatsSnap.docs.map(d => ({ flatId: d.id, ...d.data() })));

      // Payments — this flat only
      const paymentsSnap = await getDocs(query(
        collection(db, "payments"),
        where("flatId", "==", user.flatId)
      ));
      setPayments(paymentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Water bills — this flat only
      const waterSnap = await getDocs(query(
        collection(db, "waterBills"),
        where("flatId", "==", user.flatId)
      ));
      setWaterBills(waterSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Maintenance bills — this flat only (needed for the Brought Forward calc)
      const maintBillsSnap = await getDocs(query(
        collection(db, "maintenanceBills"),
        where("flatId", "==", user.flatId)
      ));
      setMaintenanceBills(maintBillsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    } catch (err) {
      console.error("TenantScreen load error:", err);
    } finally {
      setDataLoading(false);
    }
  }

  useEffect(() => { loadData(); }, [user?.uid]);

  const visibleTabs = TENANT_TABS.filter(tab => tab.id !== "water" || tenantUser.paysWater === true);

  return (
    <div style={{ background: "#0f0f13", minHeight: "100vh", fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* ── Sticky Header ── */}
      <div style={{
        background: "#1a1a24", borderBottom: "1px solid #2e2e40",
        padding: "10px 16px", position: "sticky", top: 0, zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img src={adyantamLogo} alt="Adyantam"
            style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }} />
          <div>
            <div style={{ fontSize: "12px", fontWeight: "700", color: "#6c63ff", lineHeight: 1.2 }}>
              Adyantam Building Circle
            </div>
            <div style={{ fontSize: "10px", color: "#555570" }}>
              {settings?.societyName || "Society"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#e8e8f0" }}>{user.name}</div>
            <div style={{ fontSize: "10px", color: "#06b6d4" }}>Tenant · {user.flatId}</div>
          </div>
          <button onClick={logout} style={{
            background: "#ff525220", border: "1px solid #ff525240", color: "#ff5252",
            borderRadius: "8px", padding: "6px 10px", fontSize: "11px",
            fontWeight: "600", cursor: "pointer",
          }}>Sign Out</button>
        </div>
      </div>

      {/* ── Tab Bar ── */}
      <div style={{
        background: "#1a1a24", borderBottom: "1px solid #2e2e40",
        position: "sticky", top: "53px", zIndex: 99,
        overflowX: "auto", display: "flex", padding: "0 8px",
        scrollbarWidth: "none",
      }}>
        {visibleTabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flexShrink: 0, padding: "10px 12px", fontSize: "12px", fontWeight: "500",
            color: activeTab === tab.id ? "#6c63ff" : "#8888a8",
            background: "none", border: "none", cursor: "pointer",
            borderBottom: activeTab === tab.id ? "2px solid #6c63ff" : "2px solid transparent",
            whiteSpace: "nowrap", transition: "all 0.2s",
          }}>
            <span style={{ marginRight: "4px" }}>{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "14px", maxWidth: "640px", margin: "0 auto" }}>

        {dataLoading && activeTab === "dashboard" && (
          <div style={{ textAlign: "center", padding: "40px", color: "#555570", fontSize: "13px" }}>
            Loading…
          </div>
        )}

        {!dataLoading && activeTab === "dashboard" && (
          <TenantMyDues
            user={tenantUser}
            settings={settings}
            flats={flats}
            payments={payments}
            maintenanceBills={maintenanceBills}
            waterBills={waterBills}
          />
        )}

        {activeTab === "flats" && (
          <TenantProfileTab
            user={tenantUser}
            onUpdate={updated => setTenantUser(updated)}
          />
        )}

        {activeTab === "passbook" && <TenantPassbookTab user={tenantUser} />}

        {activeTab === "water" && tenantUser.paysWater === true && (
          <WaterScreen canEdit={false} user={tenantUser} isPresident={() => false} />
        )}

        {activeTab === "poll"     && <PollScreen />}
        {activeTab === "tasks"    && <TasksScreen />}
        {activeTab === "chat"     && <ChatScreen />}
        {activeTab === "contacts" && <ContactsScreen />}
        {activeTab === "bylaws"   && <TenantBylawsTab />}
        {activeTab === "reports"  && <ReportsScreen />}

        {/* Footer */}
        <div style={{
          marginTop: "32px", paddingTop: "16px", borderTop: "1px solid #1e1e2e",
          textAlign: "center", fontSize: "10px", color: "#6666a0", lineHeight: "1.8",
        }}>
          <div style={{ fontWeight: "600", color: "#8888a8", marginBottom: "2px" }}>
            Adyantam Building Circle · Empowering Resident Communities
          </div>
          <div>© {new Date().getFullYear()} Adyantam. All rights reserved. · v1.0.0</div>
          <div style={{ marginTop: "2px", fontStyle: "italic" }}>
            you don't need to know code to build — Conceptualised by Dr. Vijay DSK, powered by AI
          </div>
        </div>
      </div>
    </div>
  );
}
